import { useEffect, useRef, useState } from 'react';
import { noteError } from '../lib/diagnostics';

/**
 * Video asset slot.
 *
 * Every film asset is still outstanding: the instruction clip with the siblings
 * squawking, the attract loop, the transition MP4. Rather than block the build,
 * this renders the real <video> when the file exists at `src` and a labelled
 * placeholder when it does not — so dropping the delivered files into
 * public/assets/ is the only step needed to go live.
 */
/**
 * Start an unmuted clip, and settle for a silent one rather than a frozen frame.
 *
 * iOS Safari refuses `autoPlay` outright when the video has sound, and a prior
 * tap elsewhere on the page does not buy it — so the instruction clip did not
 * play at all, it sat on frame one. On web that leaves a guest staring at a
 * still; on the kiosk, where the clip ending is the only way forward, it means
 * waiting out the watchdog in `InstructionScreen`.
 *
 * Muted playback is always permitted, so falling back to it keeps the clip
 * moving and keeps `onEnded` firing. The loss is survivable: the capture screen
 * that follows says "Open your mouth. Unleash your best caw." in text.
 *
 * Same lesson ResultScreen and ClipScreen each learned separately.
 */
/** Kiosk only: how many times a media error is retried before it is believed. */
const MAX_KIOSK_ATTEMPTS = 3;
const RETRY_DELAY_MS = 700;

function playWithMutedFallback(video: HTMLVideoElement): void {
  void video.play().catch(() => {
    video.muted = true;
    void video.play().catch((err: unknown) => {
      noteError('video.autoplay', err);
    });
  });
}

export function VideoPlaceholder({
  src,
  label,
  loop = false,
  muted = true,
  autoPlay = true,
  className = '',
  onEnded,
  onTimeUpdate,
  kiosk = false,
}: {
  src: string;
  label: string;
  loop?: boolean;
  muted?: boolean;
  autoPlay?: boolean;
  className?: string;
  onEnded?: () => void;
  onTimeUpdate?: (currentMs: number) => void;
  kiosk?: boolean;
}) {
  const [missing, setMissing] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const retriesRef = useRef(0);
  const retryTimerRef = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (retryTimerRef.current !== null) clearTimeout(retryTimerRef.current);
    },
    [],
  );

  /**
   * A media error on the kiosk is retried before it is believed.
   *
   * The attract loop occasionally came up as the placeholder box on the unit.
   * `onError` used to set `missing` and record nothing, so there was no way to
   * tell a missing file from a decoder that was briefly unavailable — and on
   * this board the second is the likelier: the session just finished used two
   * hardware H.264 decoders and a VP8 one, and the attract loop's own decoder
   * is requested the instant the flow resets. A file that is genuinely absent
   * fails the same way three times and still ends up as `missing`; a decoder
   * that needed a moment is back on the first retry. Either way the code is in
   * the admin log now.
   */
  const handleError = (video: HTMLVideoElement): void => {
    const err = video.error;
    const names: Record<number, string> = {
      1: 'MEDIA_ERR_ABORTED',
      2: 'MEDIA_ERR_NETWORK',
      3: 'MEDIA_ERR_DECODE',
      4: 'MEDIA_ERR_SRC_NOT_SUPPORTED',
    };
    const attempt = retriesRef.current + 1;
    noteError(
      `video.${label.toLowerCase().replace(/\s+/g, '-')}`,
      `${src}: ${err ? names[err.code] ?? `code ${err.code}` : 'error event'}${
        err?.message ? ` — ${err.message}` : ''
      } (attempt ${attempt} of ${kiosk ? MAX_KIOSK_ATTEMPTS : 1})`,
    );
    if (kiosk && attempt < MAX_KIOSK_ATTEMPTS) {
      retriesRef.current = attempt;
      retryTimerRef.current = window.setTimeout(() => {
        retryTimerRef.current = null;
        video.load();
        if (autoPlay) void video.play().catch(() => undefined);
      }, RETRY_DELAY_MS * attempt);
      return;
    }
    setMissing(true);
  };

  useEffect(() => {
    // Placeholders have no duration to end, so fake completion for the flow.
    if (!missing || !onEnded) return;
    const t = window.setTimeout(onEnded, 3000);
    return () => clearTimeout(t);
  }, [missing, onEnded]);

  // Only clips with sound need rescuing — a muted autoPlay is never refused.
  useEffect(() => {
    if (missing || muted || !autoPlay) return;
    const video = videoRef.current;
    if (!video) return;
    playWithMutedFallback(video);
  }, [missing, muted, autoPlay, src]);

  if (missing) {
    // A guest never sees the dashed box with a file path in it. On the unit a
    // missing clip is a plain field in the page's own dark, and the flow's
    // timers carry on as they would over the real clip.
    if (kiosk) return <div className={`bg-ink ${className}`} aria-label={label} />;
    return (
      <div
        className={`flex flex-col items-center justify-center gap-3 border-2 border-dashed border-bone/25 bg-moss/20 ${className}`}
      >
        <span className={`font-display uppercase text-bone/50 ${kiosk ? 'text-3xl' : 'text-lg'}`}>
          {label}
        </span>
        <span className={`text-bone/30 ${kiosk ? 'text-xl' : 'text-xs'}`} data-selectable>
          {src}
        </span>
      </div>
    );
  }

  return (
    <video
      ref={videoRef}
      src={src}
      loop={loop}
      muted={muted}
      autoPlay={autoPlay}
      playsInline
      // A silent poster-less video on iOS needs both muted and playsInline to
      // autoplay at all; the instruction clip's audio is unmuted by a tap.
      preload="auto"
      className={className}
      onEnded={onEnded}
      onError={(e) => handleError(e.currentTarget)}
      onTimeUpdate={
        onTimeUpdate
          ? (e) => onTimeUpdate((e.currentTarget as HTMLVideoElement).currentTime * 1000)
          : undefined
      }
    />
  );
}
